'use client';

import LinkButton from '@umodeapporg/ui/components/Navigation/LinkButton';
import type { TChipTone } from '@umodeapporg/ui/components/UI/Chips/chipTones';
import OutlinedChip from '@umodeapporg/ui/components/UI/Chips/OutlinedChip';
import LoadingSpinner from '@umodeapporg/ui/components/UI/LoadingSpinner';
import { MaterialSymbol } from '@umodeapporg/ui/components/UI/MaterialSymbol';
import type { SymbolCodepoints } from '@umodeapporg/ui/components/UI/MaterialSymbol/types';
import NoDataFound from '@umodeapporg/ui/components/UI/NoDataFound';
import { useTranslation } from 'react-i18next';

import { SectionTitle } from '@/components/layout/SectionTitle';
import { SURFACE_CARD, SURFACE_INSET } from '@/components/surface/surfaces';
import { MudancaDoDia, MudancaTipo } from '@/domain/fila/interfaces/fila';
import { cn } from '@/lib/utils';

/** Quantas linhas cabem sem a Inicio crescer; o resto fica no historico da fila. */
const MUDANCAS_VISIVEIS = 4;

const TIPO_TONES: Record<MudancaTipo, TChipTone> = {
  PRESENCA: 'neutral',
  MATERIA_PRIMA: 'warning',
  MAQUINA: 'warning',
  DECISAO: 'info',
  EMPRESTIMO: 'neutral',
  CORRECAO: 'info',
  CRONOMETRAGEM: 'success',
};

const TIPO_ICONS: Record<MudancaTipo, SymbolCodepoints> = {
  PRESENCA: 'groups',
  MATERIA_PRIMA: 'inventory_2',
  MAQUINA: 'build',
  DECISAO: 'how_to_reg',
  EMPRESTIMO: 'swap_horiz',
  CORRECAO: 'undo',
  CRONOMETRAGEM: 'timer',
};

/**
 * Onde o detalhe daquela mudanca mora: a presenca abre o item da celula na fila, com as
 * OPs que se mexeram; a materia-prima e a decisao abrem a ficha da OP; a correcao de um
 * cadastro abre a trilha de alteracoes, que e onde ela pode ser conferida.
 */
function detalheHref(mudanca: MudancaDoDia): string {
  if (mudanca.tipo === 'PRESENCA') {
    return `/fila?equipe=${encodeURIComponent(mudanca.detalheUid)}`;
  }
  if (mudanca.tipo === 'CORRECAO') {
    return '/settings?tab=historico';
  }
  // O que mudou foi o tempo do PRODUTO: o detalhe e a sequencia dele, filtrada pelo
  // nome, e nao uma OP (a cronometragem mexeu em todas elas de uma vez).
  if (mudanca.tipo === 'CRONOMETRAGEM') {
    return `/sequencias?search=${encodeURIComponent(mudanca.detalheUid)}`;
  }
  // O emprestimo nao tem item proprio na Fila (o `uid` dele e o do emprestimo, nao o de
  // uma equipe): a linha abre a Fila sem filtro, em vez de filtrar por um uid que nao
  // casa com nada e mostrar uma lista vazia.
  if (mudanca.tipo === 'EMPRESTIMO') {
    return '/fila';
  }
  // O `uid` da maquina e o do EVENTO, que nenhuma tela abre. O que interessa a quem le
  // e quais OPs ficaram presas, e isso e a Fila filtrada por "Máquina parada".
  if (mudanca.tipo === 'MAQUINA') {
    return '/fila?motivo=MAQUINA_PARADA';
  }
  return `/ops?op=${encodeURIComponent(mudanca.detalheUid)}`;
}

function formatTime(iso: string): string {
  const quando = new Date(iso);
  if (Number.isNaN(quando.getTime())) {
    return '-';
  }
  const hora = String(quando.getHours()).padStart(2, '0');
  const minuto = String(quando.getMinutes()).padStart(2, '0');
  return `${hora}:${minuto}`;
}

interface HomeMudancasHojeProps {
  mudancas: Array<MudancaDoDia>;
  loading: boolean;
  failed: boolean;
  onRetry: () => void;
  className?: string;
}

/**
 * O que mudou hoje: COMUNICACAO, nunca aprovacao. Quando a dona do produto le esta lista,
 * as datas ja mudaram; o que ela quer saber e quem fez, o que foi e quantas OPs se mexeram,
 * com o detalhe a um clique.
 */
export function HomeMudancasHoje({ mudancas, loading, failed, onRetry, className }: HomeMudancasHojeProps) {
  const { t } = useTranslation('common');

  const visiveis = mudancas.slice(0, MUDANCAS_VISIVEIS);
  const restantes = mudancas.length - visiveis.length;

  return (
    <section className={cn(SURFACE_CARD, 'flex flex-col gap-2.5 p-4', className)}>
      <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
        <SectionTitle>{t('home.mudancas.titulo')}</SectionTitle>
        {/* "Nada mudou hoje" so vale depois da resposta: antes dela, e quando a leitura
            falha, a contagem fica fora em vez de dizer zero. */}
        {!loading && !failed && (
          <span className="text-xs text-foreground-subtle">
            {mudancas.length > 0 ? t('home.mudancas.contagem', { n: mudancas.length }) : t('home.mudancas.subtitulo')}
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-6">
          <LoadingSpinner size={6} color="foreground-faint" />
        </div>
      ) : failed ? (
        <NoDataFound
          variant="error"
          size="sm"
          title={t('home.erroTitulo')}
          description={t('home.mudancas.erroDescricao')}
          action={{ label: t('home.tentar'), onClick: onRetry, icon: 'refresh' }}
        />
      ) : visiveis.length === 0 ? (
        // One row only: a day with no change is the NORMAL case, and the illustration
        // spent half of the first screenful saying there was nothing to say.
        <NoDataFound
          variant="empty"
          compact
          icon="history_toggle_off"
          title={t('home.mudancas.vazioTitulo')}
          description={t('home.mudancas.vazioDescricao')}
        />
      ) : (
        <ul className="flex animate-fade-in flex-col gap-1.5">
          {visiveis.map((mudanca) => (
            <li key={mudanca._id} className={cn(SURFACE_INSET, 'flex items-center gap-x-3 px-3 py-2')}>
              <MaterialSymbol icon={TIPO_ICONS[mudanca.tipo]} size={18} className="shrink-0 text-foreground-subtle" />
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="text-xs font-semibold tabular-nums text-foreground">
                    {formatTime(mudanca.quando)}
                  </span>
                  <OutlinedChip
                    label={t(`home.mudancas.tipo.${mudanca.tipo}`)}
                    tone={TIPO_TONES[mudanca.tipo]}
                    variant="soft"
                    size="xs"
                    preserveLabelCase
                  />
                  <span className="min-w-0 break-words text-xs text-foreground-subtle">{mudanca.alvo}</span>
                  {mudanca.opsQueMudaram > 0 && (
                    <span className="text-xs text-foreground-muted">
                      {t('home.mudancas.ops', { n: mudanca.opsQueMudaram })}
                    </span>
                  )}
                </div>
                <p className="text-xs leading-snug text-foreground-muted">
                  {t('home.mudancas.linha', { quem: mudanca.quem, texto: mudanca.texto })}
                </p>
              </div>

              <LinkButton
                href={detalheHref(mudanca)}
                variant="outlined"
                tone="neutral"
                size="sm"
                className="shrink-0 normal-case"
              >
                {t('home.mudancas.verDetalhe')}
                <MaterialSymbol icon="chevron_right" size={16} />
              </LinkButton>
            </li>
          ))}
        </ul>
      )}

      {restantes > 0 && (
        <span className="text-xs text-foreground-subtle">{t('home.mudancas.maisAlem', { n: restantes })}</span>
      )}
    </section>
  );
}
